import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, Route } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import { HPMSOnOptionClick, HPMSOnRenderAction } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { HpmsCustomCardEvent } from '@philips-emr/hpms-stipulator-components/lib/types';
import { Button, FetchAPI, Image, PanelAPI, Token } from '@philips-emr/tws-components';
import { ButtonTheme, Placement, Type } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnBeforeFetch, OnDynamicContentRender, OnMount } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import HistoryService from '@philips-emr/tws-core/lib/services/history-service';
import { applicationContext } from '@philips-emr/tws-foundation';

import { SolicitacoesPendentesAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';
import './solicitacoes-pendentes.css';

interface Data {
  ieIntercambio: string,
  nrSeqGuia: number,
  nrSeqAuditoria: number,
  ieFuncaoAutorizador: number;
  ieVisualizadaEstip: string;
}

const OptionKey = {
  DETALHES: 'detalhes',
};

@Controller({ fragment: FragmentNames.SOLICITACOES_PENDENTES })
class SolicitacoesPendentesController extends BaseController {

  @Inject(Aliases.PANEL_CASO_UM)
  private panelFuncaoUm: PanelAPI;
  @Inject(Aliases.PANEL_CASO_DOIS)
  private panelFuncaoDois: PanelAPI;

  @OnMount(Aliases.PANEL_SOLIC_PENDENTES)
  onMountPanel(): void {
    const { nrSeqContrato } = HistoryService.getCurrentRouteParams();
    void HttpService.get(Endpoint.GET.FUNCAO_AUTORIZADOR, { nrSeqContrato })
      .then(response => {
        const { body } = response;
        const { ieFuncaoAutorizador } = body as Data;
        this.context.set({ funcaoAutorizador: ieFuncaoAutorizador });

        switch (ieFuncaoAutorizador) {
          case 1:
            this.panelFuncaoUm.setVisible(true);
            break;
          case 2:
            this.panelFuncaoDois.setVisible(true);
            break;
        }
      });

    applicationContext.openModule('analise-atendimentos');

  }

  @OnBeforeFetch([Aliases.FETCH_CONSULTA, Aliases.FETCH_CONSULTA_DOIS])
  onBeforeFetch(event: MountEvent): void {
    const fetch = event.target as unknown as FetchAPI;
    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    nrSeqSubestipulante && fetch.setParameters({ ...fetch.getParameters(), nrSeqSubestipulante });
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_SITUACAO)
  onDynamicContentRenderFieldSituacao(event: RenderEvent): void {
    const { data, render } = event;
    const { ieIntercambio } = data as Data;

    if (ieIntercambio == 'S') {

      render(() => (
        <div className="u-token-solicitacoes-pendentes">
          <Token value={MessageService.translate('stipulator.intercambio')} type={Type.NEUTRAL} />
        </div>)
      );
    }
  }

  @HPMSOnOptionClick(Aliases.HPMS_CUSTOM_CARD)
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSeqGuia, nrSeqAuditoria } = data as unknown as Data;

    const action = { [OptionKey.DETALHES]: () => this.detalhes(nrSeqGuia, nrSeqAuditoria) };

    if (action[key]) {
      action[key]();
    }
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_VISUALIZADA)
  onDynamicContentRenderFieldVisualizada(event: RenderEvent): void {
    const { data, render } = event;
    const { ieVisualizadaEstip } = data as Data;
    if (ieVisualizadaEstip == 'S') {

      render(() => (
        <div className="u-token-solicitacoes-pendentes">
          <Token value={MessageService.translate('stipulator.visualizada')} type={Type.NOTICE} />
        </div>
      ));
    }

  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_INTERCAMBIO)
  onDynamicContentRenderFieldIntercambio(event: RenderEvent): void {
    const { data, render } = event;
    const { ieIntercambio } = data as Data;
    if (ieIntercambio == 'S') {
      render(() => (
        <Image
          alt=""
          src="/static/assets/transfer-arrows.svg"
        />
      ));
    }
  }

  @OnDynamicContentRender([Aliases.DETALHES_GUIA, Aliases.DETALHES_REQUISICAO])
  onRenderDetalhes(event: RenderEvent): void {
    this.renderButtonDetalhes(event);
  }

  @HPMSOnRenderAction([Aliases.HPMS_CUSTOM_CARD, Aliases.HPMS_CUSTOM_CARD_DOIS])
  onDynamicContentRenderCard(event: RenderEvent): void {
    this.renderButtonDetalhes(event);
  }

  private renderButtonDetalhes(event: RenderEvent): void {
    const { data, render } = event;
    const { nrSeqGuia, nrSeqAuditoria } = data as unknown as Data;

    const content = (): ReactElement => (
      <Button
        key="report"
        icon="/static/assets/report.svg"
        theme={ButtonTheme.QUIET_EMPHASIS}
        value=""
        iconPlacement={Placement.LEFT}
        onClick={() => this.detalhes(nrSeqGuia, nrSeqAuditoria)}
      />
    );
    
    render(content);
  }

  private detalhes(nrSeqGuia: number, nrSeqAuditoria: number): void {
    if (nrSeqGuia) {
      this.historyService.push(Route.SOLICITACOES_PENDENTES_DETALHES_AUTORIZACAO, { nrSeqGuia });
    } else if (nrSeqAuditoria) {
      this.historyService.push(Route.SOLICITACOES_PENDENTES_DETALHES_REQUISICAO, { nrSeqAuditoria });
    }
  }
}

export default SolicitacoesPendentesController;
